const LABELS = [
  'High Card',
  'One Pair',
  'Two Pair',
  'Three of a Kind',
  'Straight',
  'Flush',
  'Full House',
  'Four of a Kind',
  'Straight Flush',
  'Royal Flush'
];

const combinations = <T>(items: T[], count: number): T[][] => {
  if (count === 0) return [[]];
  return items.flatMap((item, index) =>
    combinations(items.slice(index + 1), count - 1).map((rest) => [item, ...rest])
  );
};

const category = (cards: string[]) => {
  const ranks = cards.map((card) => '23456789TJQKA'.indexOf(card.slice(0, -1)) + 2);
  const counts = ranks.reduce<Record<number, number>>((result, rank) => {
    result[rank] = (result[rank] || 0) + 1;
    return result;
  }, {});
  const groups = Object.values(counts).sort((a, b) => b - a);
  const unique = Object.keys(counts)
    .map(Number)
    .sort((a, b) => a - b);
  const straight =
    unique.length === 5 && (unique[4] - unique[0] === 4 || unique.join(',') === '2,3,4,5,14');
  const flush = cards.every((card) => card.slice(-1) === cards[0].slice(-1));
  if (straight && flush) return unique[0] === 10 ? 9 : 8;
  if (groups[0] === 4) return 7;
  if (groups[0] === 3 && groups[1] === 2) return 6;
  if (flush) return 5;
  if (straight) return 4;
  if (groups[0] === 3) return 3;
  if (groups[0] === 2 && groups[1] === 2) return 2;
  return groups[0] === 2 ? 1 : 0;
};

const RANKS = '23456789TJQKA';
const normalizeCard = (card: string) => card.toUpperCase().replace(/^10/, 'T');
const rankOf = (card: string) => RANKS.indexOf(normalizeCard(card).slice(0, -1)) + 2;

/**
 * A five-card hand's strength as [category, …tie-breakers], compared left to
 * right: the ranks grouped by how often they appear (quads, trips, pairs),
 * highest first, and a straight by its top card (the wheel's is the five).
 */
const strength = (cards: string[]): number[] => {
  const ranks = cards.map(rankOf);
  const counts = ranks.reduce<Record<number, number>>((result, rank) => {
    result[rank] = (result[rank] || 0) + 1;
    return result;
  }, {});
  const byCount = Object.keys(counts)
    .map(Number)
    .sort((a, b) => counts[b] - counts[a] || b - a);
  const kind = category(cards.map(normalizeCard));
  const wheel =
    byCount.length === 5 && [...byCount].sort((a, b) => a - b).join(',') === '2,3,4,5,14';
  const straight = kind === 4 || kind === 8 || kind === 9;
  return [kind, ...(straight && wheel ? [5] : byCount)];
};

const compareStrength = (a: number[], b: number[]) => {
  for (let index = 0; index < Math.max(a.length, b.length); index++) {
    const diff = (a[index] ?? 0) - (b[index] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

/** Every five-card hand the variant allows: any five in Hold'em, two hole + three board in Omaha. */
const candidateHands = (hole: string[], board: string[], variant: 'texas' | 'omaha') =>
  variant === 'omaha'
    ? combinations(hole, 2).flatMap((pair) =>
        combinations(board, 3).map((three) => [...pair, ...three])
      )
    : combinations([...hole, ...board], 5);

const validCards = (hole: string[], board: string[], variant: 'texas' | 'omaha') => {
  const all = [...hole, ...board].map(normalizeCard);
  return (
    board.length >= 3 &&
    board.length <= 5 &&
    (variant === 'texas' ? hole.length === 2 : [4, 5].includes(hole.length)) &&
    all.every((card) => /^[2-9TJQKA][CDHS]$/.test(card)) &&
    new Set(all).size === all.length
  );
};

/**
 * The five cards that make the best high hand — what the live table's winner
 * banner shows and lights — in the cards' own spelling. Display-only, like
 * snapshotHandRank; undefined when the cards cannot make a hand.
 */
export const bestFive = (
  holeCards: string[],
  board: string[],
  variant: 'texas' | 'omaha'
): string[] | undefined => {
  if (!validCards(holeCards, board, variant)) return undefined;
  return candidateHands(holeCards, board, variant).reduce<
    { hand: string[]; score: number[] } | undefined
  >((best, hand) => {
    const score = strength(hand);
    return !best || compareStrength(score, best.score) > 0 ? { hand, score } : best;
  }, undefined)?.hand;
};

/**
 * The five cards of the best eight-or-better low (aces low, five different
 * ranks, none above eight), compared from the highest card down; undefined
 * when no low qualifies.
 */
export const bestLowFive = (
  holeCards: string[],
  board: string[],
  variant: 'texas' | 'omaha'
): string[] | undefined => {
  if (!validCards(holeCards, board, variant)) return undefined;
  const lowRank = (card: string) => (rankOf(card) === 14 ? 1 : rankOf(card));
  return candidateHands(holeCards, board, variant)
    .map((hand) => ({ hand, ranks: hand.map(lowRank).sort((a, b) => b - a) }))
    .filter(({ ranks }) => new Set(ranks).size === 5 && ranks[0] <= 8)
    .reduce<{ hand: string[]; ranks: number[] } | undefined>(
      (best, candidate) =>
        !best || compareStrength(best.ranks, candidate.ranks) > 0 ? candidate : best,
      undefined
    )?.hand;
};

/** Display-only high-hand category. Never used to decide awards or expose cards.
 * Omaha (including Hi/Lo) must use exactly two hole cards and three board cards. */
export const snapshotHandRank = (
  holeCards: string[],
  board: string[],
  variant: 'texas' | 'omaha'
) => {
  const normalize = (cards: string[]) =>
    cards.map((card) => card.toUpperCase().replace(/^10/, 'T'));
  const hole = normalize(holeCards);
  const community = normalize(board);
  const all = [...hole, ...community];
  if (
    community.length < 3 ||
    community.length > 5 ||
    (variant === 'texas' ? hole.length !== 2 : ![4, 5].includes(hole.length)) ||
    all.some((card) => !/^[2-9TJQKA][CDHS]$/.test(card)) ||
    new Set(all).size !== all.length
  )
    return '';
  const hands =
    variant === 'omaha'
      ? combinations(hole, 2).flatMap((pair) =>
          combinations(community, 3).map((three) => [...pair, ...three])
        )
      : combinations(all, 5);
  return LABELS[Math.max(...hands.map(category))];
};
